// =====================================================================
//  HRMS ADMIN CONSOLE, DESIGN TOKENS
// ---------------------------------------------------------------------
//  The employee portal and the admin console are deliberately different
//  products wearing the same brand.
//
//    Employee portal   white sidebar, forest-green accent, rounded cards,
//                      a greeting in the header. Warm, personal, sparse.
//
//    Admin console     graphite command rail, hairline-separated panels,
//                      tabular numerals, 40px data rows, status chips
//                      instead of gradient tiles. Dense, neutral, and
//                      built for scanning hundreds of rows.
//
//  The console never uses decorative gradients on data surfaces. Colour
//  in this system carries meaning: it marks state (approved / pending /
//  rejected) or direction (up / down). Everything else is greyscale, so
//  the eye goes to the number that changed rather than the card it sits
//  in.
//
//  These JS tokens mirror the `--ac-*` CSS variables in admin.css. Use
//  the CSS variables for styling and this module when a value has to
//  reach JavaScript (chart series, inline SVG, canvas).
// =====================================================================

export const ACCENT = '#0EA5A4'; // product.hrms teal, the console's one interactive colour
export const BRAND_FOREST = '#1A3C2B'; // WebIdeal green, kept for the brand lockup only

/** Graphite scale for the command rail and dark surfaces. */
export const rail = {
  base: '#0F1419',
  raised: '#161C23',
  hover: '#1D242C',
  active: '#232C36',
  border: 'rgba(255,255,255,0.07)',
  text: '#E6EAEF',
  textMuted: '#8A95A3',
  textFaint: '#5E6875',
};

/** Light surfaces for the working area. */
export const surface = {
  canvas: '#F7F8F8', // page background
  panel: '#FFFFFF', // cards, tables
  sunken: '#FAFBFB', // table headers, toolbars, inset areas
  hover: '#F4F6F7', // row hover
  selected: '#EDF7F7', // row selected (accent-tinted)
  line: '#E4E7E9', // primary hairline
  lineSoft: '#EFF1F2', // internal dividers
};

/** Text scale. */
export const ink = {
  strong: '#0F1419', // headings, key numbers
  base: '#2B333C', // body / cell text
  muted: '#67717D', // labels, secondary
  faint: '#98A1AC', // placeholders, meta
};

/**
 * Semantic state colours. Each has a `fg` (text/icon), `bg` (chip fill) and
 * `bd` (chip border) so chips stay legible on white and on hovered rows.
 */
export const state = {
  positive: { fg: '#0F7B4F', bg: '#E7F6EE', bd: '#BCE5CF', solid: '#16A34A' },
  warning: { fg: '#9A6206', bg: '#FEF4E4', bd: '#F3DCAE', solid: '#E0A33E' },
  negative: { fg: '#B02A25', bg: '#FDECEB', bd: '#F5C9C6', solid: '#DC2626' },
  info: { fg: '#0B6E9E', bg: '#E6F4FB', bd: '#BEE0F1', solid: '#0EA5E9' },
  accent: { fg: '#0B7C7B', bg: '#E6F6F6', bd: '#B8E3E2', solid: ACCENT },
  violet: { fg: '#5B45B8', bg: '#F0EDFC', bd: '#D5CCF5', solid: '#7C5CF0' },
  neutral: { fg: '#4B545E', bg: '#F1F3F4', bd: '#DFE3E6', solid: '#6B7480' },
};

/**
 * Maps the free-text status values the API returns onto semantic states.
 * The database stores Title Case ('Approved', 'Pending'), but nothing
 * enforces it, so matching is case-insensitive and falls back to neutral
 * rather than rendering an unstyled chip.
 */
const STATUS_MAP = {
  // positive
  active: 'positive', approved: 'positive', present: 'positive', processed: 'positive',
  paid: 'positive', completed: 'positive', resolved: 'positive', closed: 'positive',
  hired: 'positive', accepted: 'positive',
  // warning
  pending: 'warning', late: 'warning', 'in progress': 'warning', in_progress: 'warning',
  review: 'warning', 'under review': 'warning', onboarding: 'warning', draft: 'warning',
  'half day': 'warning', screening: 'warning',
  // negative
  inactive: 'negative', rejected: 'negative', absent: 'negative', failed: 'negative',
  terminated: 'negative', overdue: 'negative', declined: 'negative', suspended: 'negative',
  // info
  'on leave': 'info', open: 'info', new: 'info', applied: 'info', submitted: 'info',
  interview: 'info', interviewing: 'info', offered: 'info',
  // violet
  remote: 'violet', 'work from home': 'violet', shortlisted: 'violet',
};

export const statusTone = (value) => {
  const key = String(value ?? '').trim().toLowerCase();
  return STATUS_MAP[key] || 'neutral';
};

/**
 * Tone name → the abbreviation used in the CSS custom properties.
 *
 * The tone keys this module speaks in are spelled out ('positive',
 * 'negative', 'neutral'), but the variables in admin.css are abbreviated
 * (`--ac-pos-bg`, `--ac-neg-bg`, `--ac-neu-bg`) to keep the hairline-heavy
 * state block readable. Anything building a variable name from a tone key
 * has to go through this map.
 *
 * It lives here rather than being re-declared per page because getting it
 * wrong fails SILENTLY: `var(--ac-positive-bg)` is simply an unknown
 * property, so CSS drops the declaration, the build passes, and the element
 * renders unstyled. That is a difficult bug to spot in review and an easy
 * one to copy.
 *
 * Usage:
 *   background: `var(--ac-${toneVar(tone)}-bg)`
 *   borderColor: `var(--ac-${toneVar(tone)}-bd)`
 *   color: `var(--ac-${toneVar(tone)}-fg)`
 */
const TONE_VAR = {
  positive: 'pos',
  negative: 'neg',
  neutral: 'neu',
  warning: 'warn',
  violet: 'vio',
  info: 'info',
  accent: 'accent',
};

export const toneVar = (tone) => TONE_VAR[tone] || 'neu';

/**
 * Ink and surface variable names, so components stop guessing at them.
 * The JS token objects above use descriptive keys (`ink.strong`), while the
 * CSS uses shorter ones (`--ac-ink`); these constants are the bridge.
 */
export const cssVar = {
  inkStrong: 'var(--ac-ink)',
  inkBase: 'var(--ac-body)',
  inkMuted: 'var(--ac-muted)',
  inkFaint: 'var(--ac-faint)',
  panel: 'var(--ac-panel)',
  canvas: 'var(--ac-canvas)',
  sunken: 'var(--ac-sunken)',
  hover: 'var(--ac-hover)',
  selected: 'var(--ac-selected)',
  line: 'var(--ac-line)',
  lineSoft: 'var(--ac-line-soft)',
  accent: 'var(--ac-accent)',
};

/** Role → tone, used for the role chips in the People directory. */
export const roleTone = (role) => {
  switch (String(role ?? '').toLowerCase()) {
    case 'admin': return 'negative';
    case 'hr': return 'violet';
    case 'manager': return 'info';
    case 'superadmin': return 'accent';
    default: return 'neutral';
  }
};

/**
 * Chart series palette. Ordered so that adjacent series stay
 * distinguishable, and muted enough that a chart never out-shouts the
 * numbers beside it.
 */
export const series = {
  present: '#16A34A',
  absent: '#DC2626',
  late: '#E0A33E',
  leave: '#0EA5E9',
  accent: ACCENT,
  violet: '#7C5CF0',
  slate: '#94A3B8',
  grid: '#E9ECEE',
  axis: '#98A1AC',
};

export const CHART_SEQUENCE = [
  ACCENT, '#7C5CF0', '#0EA5E9', '#16A34A', '#E0A33E', '#DC2626', '#94A3B8', '#0F766E',
];

const theme = { ACCENT, BRAND_FOREST, rail, surface, ink, state, series, CHART_SEQUENCE, statusTone, roleTone };
export default theme;
